import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react'
import { useAuthContext } from './AuthContext'
import { Department } from '../data/departments'
import { config } from '../config/environment'

const API_BASE_URL = config.API_BASE_URL

interface UserProfileResponse {
  exists: boolean
  userId?: string
  email?: string
  department?: Department
}

interface UserIdentityContextValue {
  isLoading: boolean
  hasProfile: boolean
  email: string | null
  department: Department | null
  userId: string | null
  saveProfile: (department: Department) => Promise<void>
}

const UserIdentityContext = createContext<UserIdentityContextValue | null>(null)

interface UserIdentityProviderProps {
  children: ReactNode
}

export function UserIdentityProvider({ children }: UserIdentityProviderProps): JSX.Element {
  const { user, isAuthenticated, isLoading: authLoading } = useAuthContext()

  const [department, setDepartment] = useState<Department | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Fetch stored department from backend once the user is authenticated
  useEffect(() => {
    if (authLoading) return

    if (!isAuthenticated || !user) {
      setDepartment(null)
      setIsLoading(false)
      return
    }

    const fetchProfile = async () => {
      setIsLoading(true)
      try {
        const response = await fetch(`${API_BASE_URL}/api/users/profile`, {
          credentials: 'include',
        })
        if (response.ok) {
          const data: UserProfileResponse = await response.json()
          if (data.exists && data.department) {
            setDepartment(data.department)
          }
        }
      } catch (error) {
        console.warn('Failed to fetch user profile:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchProfile()
  }, [isAuthenticated, user, authLoading])

  const saveProfile = async (dept: Department): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/api/users/profile`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ department: dept }),
    })

    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw new Error(err.error || 'Failed to save profile')
    }

    setDepartment(dept)
  }

  const contextValue: UserIdentityContextValue = {
    isLoading: authLoading || isLoading,
    hasProfile: !!department,
    email: user?.email ?? null,
    department,
    userId: user?.id ?? null,
    saveProfile,
  }

  return (
    <UserIdentityContext.Provider value={contextValue}>
      {children}
    </UserIdentityContext.Provider>
  )
}

export function useUserIdentityContext(): UserIdentityContextValue {
  const context = useContext(UserIdentityContext)

  if (!context) {
    throw new Error('useUserIdentityContext must be used within a UserIdentityProvider')
  }

  return context
}

/**
 * Non-throwing variant for shared components that also render OUTSIDE Studio
 * (e.g. R3Chat in the standalone Ros app, which has its own auth and no
 * UserIdentityProvider). Returns null when no provider is mounted.
 */
export function useOptionalUserIdentity(): UserIdentityContextValue | null {
  return useContext(UserIdentityContext) ?? null
}

export { UserIdentityContext }
